'use client';
import ProductsPage from '@c/ProductsPage';
import { CategoryDocument } from '@models/category';
import { getGenderLabel } from '@util/createListingHelpers';
import { trackUserSearch } from '@util/firestore/recommendations';
import { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { logError } from '@util/logError';
import { Pagination } from '@util/types/paginator';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import { ProductDocument } from 'models/product';
import { PublicUserDocument } from 'models/user';
import { useEffect, useRef } from 'react';
import { FacetCounts } from 'models/product';

interface SearchProps {
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: PageSideFilters;
  keyword: string;
  allBrands: string[];
  categories: CategoryDocument[];
  sellers: {
    [key: string]: PublicUserDocument;
  };
  facet_counts?: FacetCounts;
  mostly_parts?: boolean;
}

const Search = (props: SearchProps) => {
  const {
    initialProducts,
    initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    keyword,
    allBrands,
    categories,
    sellers,
    facet_counts,
    mostly_parts,
  } = props;

  if (
    !initialProducts ||
    !initialPageInfo ||
    !initialTopFilters ||
    !initialSideFilters
  ) {
    const el = document.getElementById('__NEXT_DATA__') as HTMLScriptElement;
    logError(
      'SearchPage props undefined ' + JSON.stringify(props),
      undefined,
      el.innerHTML
    );
  }

  const { user } = useAuth();
  const { setFilters } = useSearch();
  const ref = useRef<any>(null);
  useEffect(() => {
    if (typeof user === 'undefined') return;
    const args = {
      uid: user?.uid,
      brands: initialSideFilters.brands,
      categories: initialSideFilters.category,
      condition: initialSideFilters.conditions[0],
      gender: initialSideFilters.genders?.[0],
      inserted_time: Date.now(),
      makes: initialSideFilters.bikeMakes,
      models: initialSideFilters.bikeModels,
      on_sale: initialSideFilters.onsale,
      result_count: initialPageInfo.totalResults,
      seller_type: initialSideFilters.sellerType?.[0],
      size: initialSideFilters.letterSizes[0],
      sort: (initialTopFilters.sort === 'bestMatch'
        ? 'Default'
        : initialTopFilters.sort === 'newest'
        ? 'Newest'
        : initialTopFilters.sort === 'priceAsc'
        ? 'Low Price'
        : initialTopFilters.sort === 'priceDesc'
        ? 'High Price'
        : 'Default') as 'Default' | 'Newest' | 'Low Price' | 'High Price',
      term: keyword,
      years: initialSideFilters.bikeYears?.map((y) => +y),
      referrer: window.location.href,
      page: initialPageInfo.page,
    };
    setFilters(args);
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(() => {
      trackUserSearch(args);
    }, 1000);
  }, [
    initialPageInfo.page,
    initialPageInfo.totalResults,
    initialSideFilters.bikeMakes,
    initialSideFilters.bikeModels,
    initialSideFilters.bikeYears,
    initialSideFilters.brands,
    initialSideFilters.category,
    initialSideFilters.conditions,
    initialSideFilters.genders,
    initialSideFilters.letterSizes,
    initialSideFilters.onsale,
    initialSideFilters.sellerType,
    initialTopFilters.sort,
    keyword,
    setFilters,
    user,
  ]);

  return (
    <ProductsPage
      sellers={sellers}
      initialProducts={initialProducts}
      initialPageInfo={initialPageInfo}
      initialTopFilters={initialTopFilters}
      initialSideFilters={initialSideFilters}
      headerBreadcrumb={{ label: 'Search', href: '/search' }}
      pageHeaderTitle={
        <>
          <span className="font-bold">{initialPageInfo.totalResults}</span>{' '}
          results for <span className="font-bold">&quot;{keyword}&quot;</span>
        </>
      }
      pageType="search"
      keyword={keyword}
      seller_id={null}
      allBrands={allBrands}
      showSearch={false}
      facet_counts={facet_counts}
      mostly_parts={mostly_parts}
    />
  );
};

export default Search;
